// src/pages/admin/AdminModels.jsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { matchingModelAPI } from '../../services/api';
import { Spinner, useConfirm } from '../../components/shared/UI';
import toast from 'react-hot-toast';
import { Plus, Copy, Pencil, Trash2 } from 'lucide-react';

export const AdminModels = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renameId, setRenameId] = useState(null);
  const [renameName, setRenameName] = useState('');
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirm();

  const load = async () => {
    try {
      const { data } = await matchingModelAPI.listModels();
      setModels(data.data || []);
    } catch { /* interceptor toasts */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    try {
      const { data } = await matchingModelAPI.createModel({ modelName: 'New Model', modelVersion: '1.0' });
      toast.success('Model created');
      navigate(`/admin/matching/models/${data.data.id}`);
    } catch { /* interceptor toasts */ }
  };

  const handleDelete = async (id, name) => {
    if (!await confirm('Delete Model', `Are you sure you want to delete "${name || 'Untitled'}"? This cannot be undone.`)) return;
    try {
      await matchingModelAPI.deleteModel(id);
      setModels(prev => prev.filter(m => m.id !== id));
      toast.success('Model deleted');
    } catch { /* interceptor toasts */ }
  };

  const handleDuplicate = async (id) => {
    try {
      const { data } = await matchingModelAPI.duplicateModel(id);
      toast.success('Model duplicated');
      await load();
    } catch { /* interceptor toasts */ }
  };

  const handleRename = async (id) => {
    if (!renameName.trim()) return;
    try {
      await matchingModelAPI.updateModel(id, { modelName: renameName.trim() });
      setModels(prev => prev.map(m => m.id === id ? { ...m, modelName: renameName.trim() } : m));
      setRenameId(null);
      toast.success('Model renamed');
    } catch { /* interceptor toasts */ }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spinner size={32} /></div>;

  return (
    <div className="page-content">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1>Matching Models</h1>
        <button onClick={handleCreate} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={15} /> New Model
        </button>
      </div>

      {models.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
          <p style={{ fontSize: 14, marginBottom: 16 }}>No matching models have been created yet.</p>
          <button onClick={handleCreate} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={15} /> Create First Model
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
          {models.map(model => (
            <div key={model.id} style={{
              background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: '14px 18px',
              display: 'flex', alignItems: 'center', gap: 14,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {renameId === model.id ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input value={renameName} onChange={e => setRenameName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleRename(model.id); if (e.key === 'Escape') setRenameId(null); }}
                      className="form-input" style={{ fontSize: 14, fontWeight: 600, padding: '4px 8px' }} autoFocus />
                    <button onClick={() => handleRename(model.id)} className="btn btn-success btn-sm" style={{ padding: '4px 12px' }}>Save</button>
                    <button onClick={() => setRenameId(null)} className="btn btn-secondary btn-sm" style={{ padding: '4px 12px' }}>Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => navigate(`/admin/matching/models/${model.id}`)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', fontFamily: "'Exo 2', sans-serif" }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#0e4e95' }}>
                      {model.modelName || 'Untitled Model'}
                    </div>
                    {model.modelVersion && (
                      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Version {model.modelVersion}</div>
                    )}
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => { setRenameId(model.id); setRenameName(model.modelName || ''); }}
                  title="Rename" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#666' }}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => handleDuplicate(model.id)}
                  title="Duplicate" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#666' }}>
                  <Copy size={15} />
                </button>
                <button onClick={() => handleDelete(model.id, model.modelName)}
                  title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#CC0000' }}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {dialog}
    </div>
  );
};
